import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { Search, Trash2, Tag, ShoppingBag, Store, Loader2, Package, Filter, Edit3, Printer, CheckCircle2, AlertCircle, X, RefreshCw } from 'lucide-react';
import { quickPrintProduct, quickPushShopifyProduct, quickPushSquareProduct, openSingleLabelPrint } from '../utils/productActions';
import BarcodeScannerButton from '../components/BarcodeScannerButton';

export default function ProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [printTemplateId, setPrintTemplateId] = useState('');
  const [rowAction, setRowAction] = useState({});
  const [rowStatus, setRowStatus] = useState({});
  const [labelPreview, setLabelPreview] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // direct URL to show in lightbox
  const [priceEdit, setPriceEdit] = useState(null); // {product_id, selling_price, retail_price, quantity, syncShopify, syncSquare}
  const [priceSaving, setPriceSaving] = useState(false);
  const [error, setError] = useState('');
  const searchRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterBatch) params.append('batch_id', filterBatch);
      if (search.trim()) params.append('search', search.trim());
      const res = await apiCall(`/api/products?${params.toString()}`, { method: 'GET' });
      setProducts(res.data.products || []);
    } catch {} finally { setLoading(false); }
  }, [search, filterBatch]);

  useEffect(() => {
    apiCall('/api/batches', { method: 'GET' }).then(r => setBatches(r.data.batches || [])).catch(() => {});
    apiCall('/api/label-templates', { method: 'GET' }).then(r => {
      const list = r.data || [];
      setLabelTemplates(list);
      const two = list.find(t => (t.size || '').toLowerCase() === '2x1');
      if (two) setPrintTemplateId(two.id);
      else if (list[0]) setPrintTemplateId(list[0].id);
    }).catch(() => {});
    // Auto-focus search so USB scanner lands straight into the filter
    setTimeout(() => { searchRef.current?.focus(); }, 150);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(debouncedSearch), 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    setDeleting(productId);
    try {
      await apiCall(`/api/products/${productId}`, { method: 'DELETE' });
      setProducts(prev => prev.filter(p => p.product_id !== productId));
    } catch {
      alert('Failed to delete product');
    } finally { setDeleting(null); }
  };

  const handleQuickPrint = async (p) => {
    setError('');
    if (!printTemplateId) { setError('Select a label template first.'); return; }
    setRowAction(prev => ({ ...prev, [p.product_id]: 'print' }));
    const res = await quickPrintProduct(p, printTemplateId);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    if (!res.ok) {
      setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: false, reason: res.reason } }));
      setError(res.reason);
      return;
    }
    const tpl = labelTemplates.find(t => t.id === printTemplateId);
    const size = tpl?.size || '2x1';
    setLabelPreview({ image: res.image, product_name: p.product_name, size });
    setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: true } }));
  };

  const handleQuickPushShopify = async (p) => {
    setError('');
    setRowAction(prev => ({ ...prev, [p.product_id]: 'shopify' }));
    const res = await quickPushShopifyProduct(p);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    setRowStatus(prev => ({ ...prev, [p.product_id]: res.ok ? { ok: true, action: res.action, matched_by: res.matched_by } : { ok: false, reason: res.reason } }));
    if (!res.ok) {
      setError(res.needsReconnect ? 'Shopify missing read_publications scope. Reconnect at Configuration → Shopify.' : res.reason);
    }
  };

  const handleQuickPushSquare = async (p) => {
    setError('');
    setRowAction(prev => ({ ...prev, [p.product_id]: 'square' }));
    const res = await quickPushSquareProduct(p);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    setRowStatus(prev => ({ ...prev, [p.product_id]: res.ok ? { ok: true } : { ok: false, reason: res.reason } }));
    if (!res.ok) setError(res.reason);
  };

  const openPriceEdit = (p) => {
    const retailNum = parseFloat((p.retail_price || '').toString().replace('$', '')) || 0;
    setPriceEdit({
      product_id: p.product_id,
      product_name: p.product_name || '',
      selling_price: Number(p.selling_price || 0).toFixed(2),
      retail_price: retailNum > 0 ? retailNum.toFixed(2) : '',
      quantity: Number(p.quantity || 0),
      syncShopify: false,
      syncSquare: false,
    });
  };

  const savePriceEdit = async () => {
    if (!priceEdit) return;
    setPriceSaving(true); setError('');
    const productId = priceEdit.product_id;
    try {
      const body = {
        product_name: priceEdit.product_name,
        selling_price: Number(priceEdit.selling_price) || 0,
        retail_price: priceEdit.retail_price ? String(priceEdit.retail_price) : '',
        quantity: Number(priceEdit.quantity) || 0,
      };
      await apiCall(`/api/products/${productId}`, { method: 'PUT', data: body });
      // Optimistically update local state
      setProducts(prev => prev.map(x => x.product_id === productId ? {
        ...x,
        product_name: body.product_name || x.product_name,
        selling_price: body.selling_price,
        retail_price: body.retail_price,
        quantity: body.quantity,
        total_selling_price: body.selling_price * body.quantity,
      } : x));

      // Optional channel sync — uses the same helpers as the row push buttons.
      // From Quick Edit price modal we ONLY want to update price (no inventory bump,
      // no image dupes, no cost overwrite) — pass updateMode='price_only'.
      const updated = { ...products.find(x => x.product_id === productId), ...body };
      if (priceEdit.syncShopify) {
        const r = await quickPushShopifyProduct(updated, 'price_only');
        if (!r.ok) setError(`Shopify sync: ${r.reason}`);
      }
      if (priceEdit.syncSquare) {
        const r = await quickPushSquareProduct(updated);
        if (!r.ok) setError(prev => prev ? `${prev}; Square sync: ${r.reason}` : `Square sync: ${r.reason}`);
      }
      setPriceEdit(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to update price');
    } finally { setPriceSaving(false); }
  };

  const totalValue = products.reduce((s, p) => s + (p.total_selling_price || 0), 0);
  const totalQuantity = products.reduce((s, p) => s + (p.quantity || 0), 0);

  const [syncing, setSyncing] = useState(null);
  const syncFromShopify = async (p) => {
    if (!p.barcode) { setError('This product has no barcode — cannot sync from Shopify.'); return; }
    setSyncing(p.product_id); setError('');
    try {
      const r = await apiCall('/api/sync-shopify-product', {
        method: 'POST',
        data: { product_id: p.product_id, barcode: p.barcode },
      });
      const imgs = r.data?.images_pulled || 0;
      setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: true, action: imgs ? `synced (${imgs} image${imgs === 1 ? '' : 's'})` : 'synced' } }));
      fetchProducts();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Sync failed';
      setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: false, action: msg.slice(0, 40) } }));
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="product-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A]">Product List</h2>
            <p className="text-sm text-slate-500 mt-1">{products.length} products found</p>
          </div>
          {(() => {
            const zeroCount = products.filter(p => (Number(p.selling_price) || 0) === 0 && parseFloat((p.retail_price || '').toString().replace('$', '')) > 0).length;
            return zeroCount > 0 ? (
              <button
                onClick={async () => {
                  if (!window.confirm(`Set sale price = retail price on ${zeroCount} products with $0 sale price?`)) return;
                  try {
                    setError('');
                    const r = await apiCall('/api/products/backfill-prices', { method: 'POST' });
                    fetchProducts();
                    setError(`✓ ${r.data.message}`);
                  } catch (e) { setError(e.response?.data?.detail || 'Backfill failed'); }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold border border-amber-300 transition-colors"
                title={`${zeroCount} products have $0 sale price but a valid retail price — fix in one click`}
                data-testid="fix-zero-prices-btn"
              >
                <AlertCircle className="w-3.5 h-3.5" /> Fix {zeroCount} $0 prices
              </button>
            ) : null;
          })()}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              ref={searchRef}
              value={debouncedSearch}
              onChange={e => setDebouncedSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setSearch(debouncedSearch.trim()); } }}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none w-64"
              placeholder="Search or scan barcode…"
              data-testid="product-search-input"
            />
          </div>
          <BarcodeScannerButton
            onDetected={(code) => { setDebouncedSearch(code); setSearch(code); }}
            title="Scan to search products"
            iconOnly
            className="p-2 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white transition-colors"
            testId="product-list-scan-btn"
          />
          {/* Label template */}
          {labelTemplates.length > 0 && (
            <select
              value={printTemplateId}
              onChange={e => setPrintTemplateId(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0052FF]"
              title="Label template used by Quick Print"
              data-testid="product-list-template"
            >
              {labelTemplates.map(t => <option key={t.id} value={t.id}>{t.size || t.name}</option>)}
            </select>
          )}
          {/* Filter by batch */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={filterBatch}
              onChange={e => setFilterBatch(e.target.value)}
              className="pl-10 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none appearance-none bg-white"
              data-testid="product-batch-filter"
            >
              <option value="">All Batches</option>
              {batches.map(b => (
                <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Products</p>
          <p className="text-xl font-bold text-[#0F172A] mt-1">{products.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Qty</p>
          <p className="text-xl font-bold text-[#0F172A] mt-1">{totalQuantity}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Value</p>
          <p className="text-xl font-bold text-[#0F172A] mt-1">${totalValue.toFixed(2)}</p>
        </div>        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Batches</p>
          <p className="text-xl font-bold text-[#0F172A] mt-1">{new Set(products.map(p => p.batch_id)).size}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="product-list-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-products">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No products found</p>
          <p className="text-sm text-slate-400 mt-1">{search || filterBatch ? 'Try adjusting your filters' : 'Create products from the Product Form page'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="product-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-3 w-14"></th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Product</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Barcode</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Item #</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Batch</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Qty</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Selling Price</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Compare at</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Total</th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Actions</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.product_id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === products.length - 1 ? 'border-b-0' : ''}`}
                    data-testid={`product-row-${p.product_id}`}>
                    <td className="px-2 py-3">
                      {(() => {
                        const src = p.image_url || (Array.isArray(p.images) ? p.images[0] : '') || '';
                        return src ? (
                          <img
                            src={src}
                            alt={p.product_name}
                            className="w-10 h-10 rounded-md object-cover border border-slate-200 bg-white cursor-pointer"
                            onClick={() => setImagePreview(src)}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            data-testid={`product-thumb-${p.product_id}`}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-md border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center" title="No image">
                            <Package className="w-4 h-4 text-slate-300" />
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate('/product-form', { state: { prefillProduct: p } })}
                        className="text-left group"
                        title="Open in Product Form for quick edit / reprint"
                        data-testid={`quick-edit-name-${p.product_id}`}
                      >
                        <p className="text-sm font-medium text-[#0F172A] group-hover:text-[#0052FF] group-hover:underline transition-colors">{p.product_name}</p>
                        <p className="text-xs text-slate-400 font-mono">{p.product_id}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">{p.barcode || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-mono font-semibold">{p.item_number || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.batch_id}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.quantity || 0}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <button
                        onClick={() => openPriceEdit(p)}
                        className="px-2 py-1 rounded hover:bg-[#0052FF]/10 hover:text-[#0052FF] transition-colors -ml-2"
                        title="Click to edit price"
                        data-testid={`edit-price-${p.product_id}`}
                      >
                        ${(p.selling_price || 0).toFixed(2)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {(() => {
                        const retail = parseFloat((p.retail_price || '').toString().replace('$', ''));
                        return Number.isFinite(retail) && retail > 0 ? (
                          <span className="line-through" title="Compare-at / MSRP">${retail.toFixed(2)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">${(p.total_selling_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        {p.print_label && <span title="Print Label"><Tag className="w-3.5 h-3.5 text-[#0052FF]" /></span>}
                        {p.push_shopify && <span title="Shopify"><ShoppingBag className="w-3.5 h-3.5 text-[#96bf48]" /></span>}
                        {p.push_square && <span title="Square"><Store className="w-3.5 h-3.5 text-black" /></span>}
                        {!p.print_label && !p.push_shopify && !p.push_square && <span className="text-xs text-slate-300">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleQuickPrint(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-[#0052FF] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Quick Print"
                          data-testid={`quick-print-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'print' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleQuickPushSquare(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-black hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Push to Square"
                          data-testid={`quick-square-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'square' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Store className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleQuickPushShopify(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-[#95BF47] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Push to Shopify"
                          data-testid={`quick-shopify-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'shopify' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingBag className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => openPriceEdit(p)}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-[#0052FF] hover:text-white text-slate-600 transition-colors"
                          title="Quick Edit (title, price, sync)"
                          data-testid={`quick-edit-btn-${p.product_id}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => syncFromShopify(p)}
                          disabled={syncing === p.product_id}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-emerald-500 hover:text-white text-slate-600 transition-colors disabled:opacity-50"
                          title="Sync from Shopify (pulls images, price, description back)"
                          data-testid={`sync-shopify-btn-${p.product_id}`}>
                          {syncing === p.product_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => handleDelete(p.product_id)}
                          disabled={deleting === p.product_id}
                          className="text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50 p-1.5 ml-0.5"
                          title="Delete"
                          data-testid={`delete-product-${p.product_id}`}>
                          {deleting === p.product_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                        {rowStatus[p.product_id]?.ok === true && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300 font-semibold inline-flex items-center gap-0.5"
                            title={rowStatus[p.product_id]?.matched_by ? `matched by ${rowStatus[p.product_id].matched_by}` : ''}
                            data-testid={`row-status-ok-${p.product_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> {rowStatus[p.product_id]?.action === 'updated' ? 'updated' : (rowStatus[p.product_id]?.action === 'created' ? 'created' : 'done')}
                          </span>
                        )}
                        {rowStatus[p.product_id]?.ok === false && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-300 font-semibold inline-flex items-center gap-0.5 max-w-[140px] truncate"
                            title={rowStatus[p.product_id].reason}
                            data-testid={`row-status-fail-${p.product_id}`}>
                            <AlertCircle className="w-3 h-3 flex-shrink-0" /> {rowStatus[p.product_id].reason}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {imagePreview && (
        <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setImagePreview(null)} data-testid="product-list-image-lightbox">
          <img src={imagePreview} alt="product" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          <button onClick={() => setImagePreview(null)} className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Inline price edit modal */}
      {priceEdit && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !priceSaving && setPriceEdit(null)} data-testid="price-edit-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#0052FF]">Quick edit</p>
                <p className="text-sm font-semibold text-[#0F172A] truncate max-w-xs" title={priceEdit.product_name}>{priceEdit.product_name || '(untitled)'}</p>
              </div>
              <button onClick={() => !priceSaving && setPriceEdit(null)} className="text-slate-400 hover:text-slate-600 p-1" data-testid="price-edit-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product title</span>
                <input
                  type="text"
                  value={priceEdit.product_name}
                  onChange={e => setPriceEdit(p => ({ ...p, product_name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0052FF] focus:border-transparent outline-none text-sm"
                  data-testid="price-edit-title"
                  placeholder="Product title"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Selling price</span>
                    {(() => {
                      const retail = Number(priceEdit.retail_price) || 0;
                      if (retail <= 0) return null;
                      const sell = Number(priceEdit.selling_price) || 0;
                      const computedPct = sell > 0 ? Math.round(((retail - sell) / retail) * 100) : '';
                      return (
                        <div className="flex items-center gap-1" title="Type a discount % off MSRP — selling price will update automatically.">
                          <input
                            type="number"
                            step="1"
                            value={computedPct}
                            onChange={e => {
                              const pct = Number(e.target.value);
                              if (!Number.isFinite(pct)) return;
                              const newSell = Math.round(retail * (1 - pct / 100) * 100) / 100;
                              setPriceEdit(p => ({ ...p, selling_price: Math.max(0, newSell).toFixed(2) }));
                            }}
                            className="w-12 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-right"
                            data-testid="price-edit-discount-input"
                            placeholder="0"
                          />
                          <span className="text-[11px] font-bold text-emerald-700">% OFF</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={priceEdit.selling_price}
                      onChange={e => setPriceEdit(p => ({ ...p, selling_price: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0052FF] focus:border-transparent outline-none text-sm"
                      data-testid="price-edit-selling"
                      autoFocus
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Compare-at / MSRP</span>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={priceEdit.retail_price}
                      onChange={e => setPriceEdit(p => ({ ...p, retail_price: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0052FF] focus:border-transparent outline-none text-sm"
                      placeholder="(optional)"
                      data-testid="price-edit-retail"
                    />
                  </div>
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quantity on hand</span>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={priceEdit.quantity}
                  onChange={e => setPriceEdit(p => ({ ...p, quantity: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0052FF] focus:border-transparent outline-none text-sm"
                  data-testid="price-edit-quantity"
                />
              </label>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Also update on sales channels</p>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={priceEdit.syncShopify}
                      onChange={e => setPriceEdit(p => ({ ...p, syncShopify: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-[#95BF47] focus:ring-[#95BF47]"
                      data-testid="price-edit-sync-shopify"
                    />
                    <ShoppingBag className="w-4 h-4 text-[#95BF47]" />
                    <span>Push changes to Shopify</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={priceEdit.syncSquare}
                      onChange={e => setPriceEdit(p => ({ ...p, syncSquare: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-black focus:ring-slate-800"
                      data-testid="price-edit-sync-square"
                    />
                    <Store className="w-4 h-4 text-black" />
                    <span>Push changes to Square</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                onClick={savePriceEdit}
                disabled={priceSaving}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60"
                data-testid="price-edit-save"
              >
                {priceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
              </button>
              <button onClick={() => !priceSaving && setPriceEdit(null)}
                className="px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-white text-sm font-medium"
                data-testid="price-edit-cancel"
                disabled={priceSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label preview modal */}
      {labelPreview && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLabelPreview(null)} data-testid="product-list-label-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#0052FF]">Label ready</p>
                <p className="text-sm font-semibold text-[#0F172A] truncate max-w-xs">{labelPreview.product_name}</p>
              </div>
              <button onClick={() => setLabelPreview(null)} className="text-slate-400 hover:text-slate-600 p-1" data-testid="product-list-label-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 bg-slate-50 flex items-center justify-center">
              <img src={labelPreview.image} alt="label" className="max-w-full max-h-[50vh] border border-slate-200 bg-white shadow-sm" />
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-white">
              <button
                onClick={() => openSingleLabelPrint(labelPreview.image, `Label · ${labelPreview.product_name}`, labelPreview.size)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors"
                data-testid="product-list-label-print"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={() => setLabelPreview(null)}
                className="px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium"
                data-testid="product-list-label-cancel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
